// New-patient / edit-patient modals, ported from openNewPatientModal /
// submitNewPatient / openEditPatientModal (source lines 3483-3605, 3975-4024).
// Sequence preview is now computed client-side (list already-known patients
// for the site) rather than calling a dedicated preview endpoint - the
// server still authoritatively computes/validates the real sequence on
// create (see patients service's nextSequenceForSite).
import { patientsApi, sitesApi, studiesApi } from "./api-client.js";
import { escapeHtml, openModal, closeModal, notify, $ } from "./dom.js";
import { STATE, canWriteSite } from "./state.js";
import { SEX_OPTIONS, REPROD_STATUS_OPTIONS, TUMOR_TYPES, SPECIES_OPTIONS } from "@woof/shared";
import { renderView, refreshSidebar, navigate } from "./router.js";

function nextSeqPreview(patients: any[], siteid: string): number {
  const used = patients.filter((p) => p.siteid === siteid).map((p) => p.sequence);
  return used.length === 0 ? 1 : Math.max(...used) + 1;
}

export async function openNewPatientModal(): Promise<void> {
  if (!STATE.user) return;
  const [sites, patients, studies] = await Promise.all([sitesApi.list(), patientsApi.list(), studiesApi.list()]);
  const writableSites = sites.filter((s: any) => canWriteSite(s.code));
  if (writableSites.length === 0) {
    notify("error", "Vous n'avez pas le droit de créer un patient.");
    return;
  }
  const defaultSite = writableSites[0].code;
  const seqPreview = () => nextSeqPreview(patients, ($("#np_site") as HTMLSelectElement).value);
  const idPreview = () => `${($("#np_site") as HTMLSelectElement).value}-${String(seqPreview()).padStart(2, "0")}`;

  const sitesOpts = writableSites.map((s: any) => `<option value="${s.code}" ${s.code === defaultSite ? "selected" : ""}>${s.code} — ${escapeHtml(s.name)}</option>`).join("");
  const sexOpts = SEX_OPTIONS.map((s) => `<option value="${s.code}">${s.label}</option>`).join("");
  const reprodOpts = REPROD_STATUS_OPTIONS.map((s) => `<option value="${s.code}">${s.label}</option>`).join("");
  const tumorOpts = TUMOR_TYPES.map((t) => `<option value="${t.key}">${t.label}</option>`).join("");
  const speciesOpts = SPECIES_OPTIONS.map((s) => `<option value="${s.key}">${s.label}</option>`).join("");
  // Pre-select (not lock) the study the user is currently working in, so a
  // patient created from within a study defaults into it rather than
  // defaulting to unassigned - still changeable via the dropdown.
  const studyOpts = studies.map((s: any) => `<option value="${s.id}" ${s.id === STATE.currentStudyId ? "selected" : ""}>${escapeHtml(s.name)}</option>`).join("");

  const body = `
    <div class="info-box">Format ID Patient : Site-Séquence (xx-xx). L'ID exact est attribué par le serveur à la création.</div>
    <div class="form-grid cols-2">
      <div class="form-group"><label>Site <span class="required-mark">*</span></label><select id="np_site">${sitesOpts}</select></div>
      <div class="form-group"><label>ID patient (aperçu)</label><input type="text" id="np_idPreview" value="${defaultSite}-${String(nextSeqPreview(patients, defaultSite)).padStart(2, "0")}" disabled style="background:#eff6ff;color:#1e3a8a;font-weight:700"></div>
      <div class="form-group"><label>Initiales (facultatif)</label><input type="text" id="np_init" maxlength="4" style="text-transform:uppercase"></div>
      <div class="form-group"><label>Espèce</label><select id="np_species">${speciesOpts}</select></div>
      <div class="form-group"><label>Race</label><input type="text" id="np_breed"></div>
      <div class="form-group"><label>Date de naissance</label><input type="date" id="np_dob"></div>
      <div class="form-group"><label>Sexe</label><select id="np_sex"><option value="">—</option>${sexOpts}</select></div>
      <div class="form-group"><label>Statut reproducteur</label><select id="np_reprod"><option value="">—</option>${reprodOpts}</select></div>
      <div class="form-group" style="grid-column:1/-1"><label>Type de tumeur</label><select id="np_tumor"><option value="">—</option>${tumorOpts}</select></div>
      <div class="form-group" style="grid-column:1/-1"><label>Étude clinique</label><select id="np_study"><option value="">—</option>${studyOpts}</select></div>
      <div class="form-group" style="grid-column:1/-1"><label>Notes</label><textarea id="np_notes" rows="2"></textarea></div>
    </div>`;
  openModal("Nouveau patient", body, `<button class="btn btn-secondary" id="npCancel">Annuler</button><button class="btn btn-primary" id="npSave">Créer le patient</button>`);
  $("#npCancel")!.addEventListener("click", closeModal);
  $("#np_site")!.addEventListener("change", () => {
    ($("#np_idPreview") as HTMLInputElement).value = idPreview();
  });
  $("#npSave")!.addEventListener("click", async () => {
    const siteid = ($("#np_site") as HTMLSelectElement).value;
    const init = ($("#np_init") as HTMLInputElement).value.trim();
    if (init && !/^[A-Za-z]{1,4}$/.test(init)) {
      notify("error", "Les initiales doivent être 1 à 4 lettres (ou laisser vide).");
      return;
    }
    const studyIdRaw = ($("#np_study") as HTMLSelectElement).value;
    try {
      const patient = await patientsApi.create({
        siteid,
        initials: init || undefined,
        breed: ($("#np_breed") as HTMLInputElement).value.trim() || undefined,
        brthdtc: ($("#np_dob") as HTMLInputElement).value || undefined,
        sex: ($("#np_sex") as HTMLSelectElement).value || undefined,
        reprodStatus: ($("#np_reprod") as HTMLSelectElement).value || undefined,
        tumorType: ($("#np_tumor") as HTMLSelectElement).value || "other",
        notes: ($("#np_notes") as HTMLTextAreaElement).value.trim() || undefined,
        studyId: studyIdRaw ? Number(studyIdRaw) : undefined,
        species: ($("#np_species") as HTMLSelectElement).value,
      });
      closeModal();
      navigate({ view: "ecrf", currentPatientId: patient.id, currentVisitKey: null, currentSection: null });
    } catch (err) {
      notify("error", err instanceof Error ? err.message : "Erreur de création");
    }
  });
}

export async function openEditPatientModal(p: any): Promise<void> {
  const sexOpts = SEX_OPTIONS.map((s) => `<option value="${s.code}" ${p.sex === s.code ? "selected" : ""}>${s.label}</option>`).join("");
  const reprodOpts = REPROD_STATUS_OPTIONS.map((s) => `<option value="${s.code}" ${p.reprodStatus === s.code ? "selected" : ""}>${s.label}</option>`).join("");
  const tumorOpts = TUMOR_TYPES.map((t) => `<option value="${t.key}" ${p.tumorType === t.key ? "selected" : ""}>${t.label}</option>`).join("");
  const speciesOpts = SPECIES_OPTIONS.map((s) => `<option value="${s.key}" ${p.species === s.key ? "selected" : ""}>${s.label}</option>`).join("");
  const statuses = [
    ["SCREENING", "Screening"], ["ACTIVE", "Actif"], ["COMPLETED", "Terminé"],
    ["WITHDRAWAL BY SUBJECT", "Sortie d'étude"], ["SCREEN FAILURE", "Screen Failure"],
  ];
  const stOpts = statuses.map(([k, l]) => `<option value="${k}" ${p.status === k ? "selected" : ""}>${l}</option>`).join("");
  const studies = await studiesApi.list();
  const studyOpts = studies.map((s: any) => `<option value="${s.id}" ${p.studyId === s.id ? "selected" : ""}>${escapeHtml(s.name)}</option>`).join("");

  const body = `
    <div class="form-grid cols-2">
      <div class="form-group"><label>ID Patient</label><input value="${escapeHtml(p.id)}" disabled></div>
      <div class="form-group"><label>Statut</label><select id="ep_status">${stOpts}</select></div>
      <div class="form-group"><label>Initiales</label><input id="ep_init" value="${escapeHtml(p.initials || "")}" maxlength="4" style="text-transform:uppercase"></div>
      <div class="form-group"><label>Espèce</label><select id="ep_species">${speciesOpts}</select></div>
      <div class="form-group"><label>Race</label><input id="ep_breed" value="${escapeHtml(p.breed || "")}"></div>
      <div class="form-group"><label>Date de naissance</label><input type="date" id="ep_dob" value="${p.brthdtc || ""}"></div>
      <div class="form-group"><label>Sexe</label><select id="ep_sex"><option value="">—</option>${sexOpts}</select></div>
      <div class="form-group"><label>Statut reproducteur</label><select id="ep_reprod"><option value="">—</option>${reprodOpts}</select></div>
      <div class="form-group"><label>Type de tumeur</label><select id="ep_tumor"><option value="">—</option>${tumorOpts}</select></div>
      <div class="form-group"><label>Étude clinique</label><select id="ep_study"><option value="">—</option>${studyOpts}</select></div>
      <div class="form-group" style="grid-column:1/-1"><label>Notes</label><textarea id="ep_notes" rows="2">${escapeHtml(p.notes || "")}</textarea></div>
    </div>`;
  openModal("Éditer le patient — " + p.id, body, `<button class="btn btn-secondary" id="epCancel">Annuler</button><button class="btn btn-primary" id="epSave">Enregistrer</button>`);
  $("#epCancel")!.addEventListener("click", closeModal);
  $("#epSave")!.addEventListener("click", async () => {
    const newInit = ($("#ep_init") as HTMLInputElement).value.trim().toUpperCase();
    if (newInit && !/^[A-Z]{1,4}$/.test(newInit)) {
      notify("error", "Les initiales doivent être 1 à 4 lettres (ou laisser vide).");
      return;
    }
    const newStatus = ($("#ep_status") as HTMLSelectElement).value;
    const studyIdRaw = ($("#ep_study") as HTMLSelectElement).value;
    try {
      await patientsApi.update(p.id, {
        initials: newInit,
        breed: ($("#ep_breed") as HTMLInputElement).value.trim(),
        brthdtc: ($("#ep_dob") as HTMLInputElement).value || null,
        sex: ($("#ep_sex") as HTMLSelectElement).value,
        reprodStatus: ($("#ep_reprod") as HTMLSelectElement).value,
        tumorType: ($("#ep_tumor") as HTMLSelectElement).value,
        notes: ($("#ep_notes") as HTMLTextAreaElement).value.trim(),
        studyId: studyIdRaw ? Number(studyIdRaw) : null,
        species: ($("#ep_species") as HTMLSelectElement).value,
      });
      if (newStatus !== p.status) await patientsApi.setStatus(p.id, newStatus);
    } catch (err) {
      notify("error", err instanceof Error ? err.message : "Erreur de mise à jour");
      return;
    }
    closeModal();
    await refreshSidebar();
    await renderView();
  });
}
